import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * Cache media only; prices, inventory and cart variant IDs always come from the current section response.
 * Each product component owns its gallery state, including when the gallery element is replaced.
 * @typedef {object} GalleryState
 * @property {Map<string, {html: string, signature: string}>} cache
 * @property {number} generation
 * @property {MediaGallery} current
 * @property {string} key
 */
/** @type {WeakMap<Element, GalleryState>} */
const galleryStates = new WeakMap();
const MAX_CACHED_GALLERIES = 8;

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    const owner = this.closest('product-component') ?? target;

    if (owner) {
      let state = galleryStates.get(owner);
      if (!state) {
        state = { cache: new Map(), generation: 0, current: this, key: '' };
        galleryStates.set(owner, state);
      }
      this.#state = state;
      state.current = this;
      state.key = this.#galleryKey();
      this.#cacheGallery(state.key, this);
    }

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** @type {GalleryState | undefined} */
  #state;

  #galleryKey() {
    return this.dataset.productId && this.dataset.optionValues
      ? `${this.dataset.productId}:${this.dataset.optionValues}`
      : '';
  }

  /** @param {Element} picker */
  #selectionKey(picker) {
    const values = (this.#state?.current.dataset.optionValues ?? '').split(',');
    const selected = picker.querySelectorAll('select option:checked, fieldset input:checked');

    selected.forEach((option, index) => {
      if (!(option instanceof HTMLElement)) return;
      const position = Number.parseInt(option.dataset.inputId?.split('-')[0] ?? '') - 1;
      values[Number.isNaN(position) ? index : position] = option.dataset.optionValueId ?? '';
    });

    return picker instanceof HTMLElement && picker.dataset.productId && values.every(Boolean)
      ? `${picker.dataset.productId}:${values.join(',')}`
      : '';
  }

  /** @param {HTMLElement} gallery */
  #mediaSignature(gallery) {
    return JSON.stringify([
      gallery.className, gallery.style.cssText, gallery.dataset.presentation,
      Array.from(gallery.querySelectorAll('.product-media')).map((media) => [
        media.getAttribute('data-media-id'),
        Array.from(media.querySelectorAll('img')).map((image) => [
          image.getAttribute('src'), image.getAttribute('srcset'), image.getAttribute('sizes'),
        ]),
        Array.from(media.querySelectorAll('template')).map((template) => template.innerHTML),
      ]),
    ]);
  }

  /** @param {string} key @param {HTMLElement} gallery */
  #cacheGallery(key, gallery) {
    if (!key || !this.#state || (typeof Shopify !== 'undefined' && Shopify.designMode)) return;
    const { cache } = this.#state;
    cache.delete(key);
    cache.set(key, { html: gallery.outerHTML, signature: this.#mediaSignature(gallery) });
    if (cache.size > MAX_CACHED_GALLERIES) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey) cache.delete(oldestKey);
    }
  }

  /** @param {MediaGallery} next @param {string} key */
  #showGallery(next, key) {
    const state = this.#state;
    if (!state?.current.isConnected) return;
    state.current.replaceWith(next);
    state.key = key;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    const owner = this.closest('product-component');
    if (owner && event.target.closest('product-component') !== owner) return;

    const state = this.#state;
    if (!state) return;
    const generation = ++state.generation;
    const connectedUrl = event.detail?.connectedProductUrl;
    const changesProduct = connectedUrl && event.target instanceof HTMLElement
      && connectedUrl !== event.target.dataset.productUrl?.split('?')[0];
    const key = changesProduct ? '' : this.#selectionKey(event.target);
    const cached = state.cache.get(key);

    if (cached && state.key !== key) {
      const template = document.createElement('template');
      template.innerHTML = cached.html;
      const preview = /** @type {MediaGallery | null} */ (template.content.querySelector('media-gallery'));
      if (preview) {
        this.#showGallery(preview, key);
      }
    }

    event.promise
      .then(({ detail }) => {
        if (state.generation !== generation || !state.current.isConnected) return;
        const next = /** @type {MediaGallery | null | undefined} */ (detail?.html?.querySelector('media-gallery'));
        if (!next) return;
        const nextKey = next.dataset.productId && next.dataset.optionValues
          ? `${next.dataset.productId}:${next.dataset.optionValues}`
          : key;
        // A cached album already on screen needs no second replacement when its media is unchanged.
        if (state.key === nextKey && state.cache.get(nextKey)?.signature === this.#mediaSignature(next)) {
          return;
        }
        this.#cacheGallery(nextKey, next);
        this.#showGallery(next, nextKey);
      })
      .catch((error) => {
        if (state.generation !== generation || !state.current.isConnected) return;
        if (error?.name !== 'AbortError') {
          console.warn('[media-gallery] Variant update failed:', error);
        }
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
